import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import axios from 'axios';
import { Sparkles, ExternalLink, Eye } from 'lucide-react';

const SpotlightArticles = () => {
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchSpotlights();
  }, []);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (spotlights.length <= 1) return; // Don't rotate if only one or no spotlights

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % spotlights.length);
    }, 6000); // 6 seconds

    return () => clearInterval(interval);
  }, [spotlights.length]);

  const fetchSpotlights = async () => {
    try {
      const response = await axios.get(`${API}/advertisements/active?ad_type=spotlight_article`);
      setSpotlights(response.data);
    } catch (error) {
      console.error('Failed to fetch spotlight articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async (adId) => {
    try {
      await axios.post(`${API}/advertisements/${adId}/track`, { action: 'click' });
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  const trackView = async (adId) => {
    try {
      await axios.post(`${API}/advertisements/${adId}/track`, { action: 'view' });
    } catch (error) {
      console.error('Failed to track view:', error);
    }
  };

  useEffect(() => {
    // Track views for all loaded spotlights
    spotlights.forEach(spotlight => {
      trackView(spotlight.id);
    });
  }, [spotlights]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 mx-auto"></div>
      </div>
    );
  }

  if (spotlights.length === 0) {
    return null; // Don't show section if no spotlights
  }

  const currentSpotlight = spotlights[currentIndex];

  return (
    <section className="py-16">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Sparkles className="w-8 h-8 text-rose-600" />
          <h2 className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Spotlight
          </h2>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Featured stories and insights from our community partners
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Spotlight Card with smooth transition */}
        <div className="relative transition-all duration-500 ease-in-out">
          <Card key={currentSpotlight.id} className="glass hover:shadow-xl transition-all animate-fade-in">
            <div className="relative">
              <Badge className="absolute top-4 right-4 bg-rose-600 text-white z-10">
                <Sparkles className="w-3 h-3 mr-1" />
                Sponsored
              </Badge>
              {currentSpotlight.image_url && (
                <img 
                  src={currentSpotlight.image_url} 
                  alt={currentSpotlight.title}
                  className="w-full h-64 object-cover rounded-t-lg"
                />
              )}
            </div>
            <CardHeader>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {currentSpotlight.title}
              </CardTitle>
              <CardDescription>
                By {currentSpotlight.advertiser_name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {currentSpotlight.content && (
                <p className="text-gray-700 mb-6 line-clamp-4">
                  {currentSpotlight.content}
                </p>
              )}
              {currentSpotlight.link_url && (
                <Button 
                  onClick={() => {
                    trackClick(currentSpotlight.id);
                    window.open(currentSpotlight.link_url, '_blank');
                  }}
                  className="w-full bg-rose-600 hover:bg-rose-700 flex items-center justify-center gap-2"
                >
                  Read More
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
              <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {currentSpotlight.views || 0} views
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation Dots */}
        {spotlights.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {spotlights.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-rose-600 w-8' 
                    : 'bg-rose-200 hover:bg-rose-400'
                }`}
                aria-label={`Go to spotlight ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default SpotlightArticles;
